
import React, { createContext, useContext, useState, useCallback } from 'react';
import { Backdrop, CircularProgress, Typography, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const LoaderContext = createContext();

export const useLoader = () => {
    const context = useContext(LoaderContext);
    if (!context) {
        throw new Error('useLoader must be used within a LoaderProvider');
    }
    return context;
};

export const LoaderProvider = ({ children }) => {
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const theme = useTheme();

    const showLoader = useCallback((msg = 'Loading...') => {
        setMessage(msg);
        setLoading(true);
    }, []);

    const hideLoader = useCallback(() => {
        setLoading(false);
        setMessage('');
    }, []);

    return (
        <LoaderContext.Provider value={{ showLoader, hideLoader, isLoading: loading }}>
            {children}
            <Backdrop
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 9999, // Highest z-index
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    backdropFilter: 'blur(5px)',
                    backgroundColor: 'rgba(0, 0, 0, 0.4)'
                }}
                open={loading}
            >
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {/* Outer pulsing circle */}
                    <Box sx={{
                        position: 'absolute',
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        border: `4px solid ${theme.palette.primary.main}`,
                        opacity: 0.3,
                        animation: 'pulse 1.5s ease-in-out infinite'
                    }} />

                    <CircularProgress
                        size={60}
                        thickness={4}
                        sx={{
                            color: theme.palette.secondary.main,
                            '& .MuiCircularProgress-circle': {
                                strokeLinecap: 'round',
                            },
                        }}
                    />
                </Box>

                <Typography
                    variant="h6"
                    sx={{
                        color: 'white',
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        animation: 'fadeIn 0.5s ease-in-out'
                    }}
                >
                    {message}
                </Typography>

                <style>
                    {`
                        @keyframes pulse {
                            0% { transform: scale(0.8); opacity: 0.5; }
                            100% { transform: scale(1.5); opacity: 0; }
                        }
                        @keyframes fadeIn {
                            from { opacity: 0; transform: translateY(10px); }
                            to { opacity: 1; transform: translateY(0); }
                        }
                    `}
                </style>
            </Backdrop>
        </LoaderContext.Provider>
    );
};
